/**
 * Knowledge Base for the Balancio AI Agent
 * Contains static knowledge about the application's features, accounting concepts,
 * and how-to guides that the AI can reference when answering questions.
 */

export interface KnowledgeEntry {
  id: string;
  topic: string;
  keywords: string[];
  content: string;
  category: 'app_feature' | 'accounting_concept' | 'how_to' | 'faq';
}

export const knowledgeBase: KnowledgeEntry[] = [
  // ============ APP FEATURES ============
  {
    id: 'app-overview',
    topic: 'What is Balancio Pro',
    keywords: ['balancio', 'app', 'overview', 'what is', 'purpose'],
    content: `Balancio Pro is a double-entry bookkeeping and accounting web application. It allows users to:
- Create and manage multiple accounting records (like separate company books)
- Record transactions with rich semantics (actions, objects, media/payment types)
- View financial statements: Balance Sheet, Profit & Loss, Ledgers, Trading Accounts, Stock Valuation
- Collaborate with other users via record permissions
- Purchase credit points and subscribe to premium plans
- Track inventory through stock valuation features`,
    category: 'app_feature',
  },
  {
    id: 'records-feature',
    topic: 'Records',
    keywords: ['record', 'records', 'accounting record', 'container'],
    content: `A "Record" in Balancio Pro is a top-level container for a set of financial transactions. Think of it as a company's book or ledger. Each record has:
- A name and optional description
- A currency code
- A creator/owner
- Multiple users can be granted read or write permissions to a record
- Records can be exported/imported as JSON
- Users can join a record using a join code

Key operations: Create, Edit, Delete, Join by Code, Import/Export, Manage Permissions`,
    category: 'app_feature',
  },
  {
    id: 'transactions-feature',
    topic: 'Transactions',
    keywords: ['transaction', 'transactions', 'entry', 'double entry'],
    content: `Transactions in Balancio Pro follow a semantic double-entry model. Each transaction has:
- Date: When the transaction occurred
- Action: What happened (e.g., "Sold", "Bought", "Paid") - a verb
- Object: What was affected (e.g., "Product A", "Service B") - a noun
- Indirect Object: Optionally, the indirect recipient/source (e.g., "Customer X", "Supplier Y")
- Media: The payment/transfer method (e.g., "Cash", "Bank Transfer", "Credit Card")
- Quantity: Number of units
- Total Amount: The monetary value

The combination of Action + Object + Media creates a natural language description like "Sold Product A for Cash"`,
    category: 'app_feature',
  },
  {
    id: 'transaction-entities-feature',
    topic: 'Transaction Entities',
    keywords: ['entity', 'entities', 'action', 'object', 'media', 'category'],
    content: `Transaction Entities are the building blocks of transactions. They come in four categories:
1. Actions (verbs): What was done - e.g., "Sold", "Bought", "Paid", "Received", "Transferred"
2. Objects (nouns - direct): What was directly affected - e.g., "Product A", "Shares", "Service"
3. Indirect Objects (nouns - indirect): The secondary entity - e.g., "Customer", "Supplier", "Employee"
4. Media: The payment/transfer method - e.g., "Cash", "Bank Account", "Credit Card", "Mobile Money"

Entities can have types (credit, debit, tradable, non_tradable, payable, third_party) and past tense forms.
The app supports semantic/cosine similarity search for finding entities.`,
    category: 'app_feature',
  },
  {
    id: 'accounts-feature',
    topic: 'Accounts',
    keywords: ['account', 'accounts', 'ledger', 'financial statements'],
    content: `Balancio Pro provides several financial account views:

1. Ledgers: Entity-based financial summaries showing balances (debit/credit amounts and quantities)
2. Stock Valuation: Inventory tracking with purchase/sale history and running balance valuation
3. Trading Accounts: Summary of trading activities with debit/credit breakdown
4. Profit & Loss Statement: Shows total revenues (credits) and expenses (debits) over a period
5. Balance Sheet: Shows Assets, Liabilities, and Capital following the accounting equation

All accounts support date range filtering.`,
    category: 'app_feature',
  },
  {
    id: 'credits-feature',
    topic: 'Credit Points',
    keywords: ['credit', 'credits', 'points', 'balance', 'purchase', 'payment'],
    content: `Credit Points are Balancio Pro's in-app currency. Users can:
- View their credit balance
- See credit transaction history
- Purchase credits using Flutterwave or Stripe payment gateways
- Use credits for subscriptions and premium features

Credit transactions are recorded with type, amount, and description.`,
    category: 'app_feature',
  },
  {
    id: 'subscriptions-feature',
    topic: 'Subscriptions',
    keywords: ['subscription', 'plan', 'subscribe', 'premium', 'features'],
    content: `Balancio Pro offers subscription plans with different features:
- Plans have different durations (monthly, yearly), prices, and feature sets
- Users can view available plans and their current subscription
- Subscription can be purchased using credit points or direct payment via Flutterwave/Stripe
- Active subscriptions grant access to premium features`,
    category: 'app_feature',
  },
  {
    id: 'dashboard-feature',
    topic: 'Dashboard KPIs',
    keywords: ['dashboard', 'kpi', 'kpis', 'metrics', 'performance', 'summary'],
    content: `The Dashboard displays Key Performance Indicators (KPIs) that provide a snapshot of financial health. KPIs can include metrics like total revenue, expenses, profit margins, and other custom indicators linked to transaction entities.`,
    category: 'app_feature',
  },
  {
    id: 'notifications-feature',
    topic: 'Notifications',
    keywords: ['notification', 'notifications', 'alert', 'alerts'],
    content: `The notifications system sends alerts to users about various events. Notifications can be marked as read individually or all at once. Types include info, success, warning, and error.`,
    category: 'app_feature',
  },

  // ============ ACCOUNTING CONCEPTS ============
  {
    id: 'double-entry-basics',
    topic: 'Double-Entry Bookkeeping Basics',
    keywords: ['double entry', 'debit', 'credit', 'accounting equation', 'bookkeeping'],
    content: `Double-entry bookkeeping is a method where every transaction affects at least two accounts. Key principles:
- Accounting Equation: Assets = Liabilities + Capital (Equity)
- Every transaction has a debit side and a credit side
- Debits must always equal Credits
- For asset accounts: debits increase the balance, credits decrease it
- For liability and capital accounts: credits increase the balance, debits decrease it
- The system ensures books always balance

In Balancio Pro, each transaction's action determines whether it's a debit or credit entry.`,
    category: 'accounting_concept',
  },
  {
    id: 'ledger-concept',
    topic: 'Ledgers',
    keywords: ['ledger', 'ledgers', 'general ledger'],
    content: `A ledger is a book or collection of accounts where all transactions are recorded. In Balancio Pro:
- Each transaction entity (action/object combination) can be viewed as a ledger
- Ledgers show the balance (amount and quantity) for each entity
- Ledgers can be filtered by date range
- They show both debit and credit transactions affecting that entity`,
    category: 'accounting_concept',
  },
  {
    id: 'balance-sheet-concept',
    topic: 'Balance Sheet',
    keywords: ['balance sheet', 'assets', 'liabilities', 'capital', 'equity'],
    content: `A Balance Sheet is a financial statement that reports a company's financial position at a specific point in time. It shows:
1. Assets: What the company owns (cash, inventory, equipment, receivables)
2. Liabilities: What the company owes (loans, payables, debts)
3. Capital/Equity: The owner's stake (initial investment + retained earnings)

The fundamental equation: Assets = Liabilities + Capital

In Balancio Pro, the balance sheet breaks down assets by media type (cash, bank, etc.), stock value, prepaid expenses, and debitors. Liabilities and capital are shown as categorized amounts.`,
    category: 'accounting_concept',
  },
  {
    id: 'profit-loss-concept',
    topic: 'Profit & Loss Statement',
    keywords: ['profit', 'loss', 'pnl', 'income', 'expenses', 'revenue'],
    content: `A Profit & Loss (P&L) statement shows financial performance over a period. It summarizes:
- Debits (expenses/losses): Money spent or value consumed
- Credits (revenues/gains): Money earned or value generated
- Net Profit/Loss: Credits - Debits

In Balancio Pro, the P&L breaks down debits and credits by entity category.`,
    category: 'accounting_concept',
  },
  {
    id: 'stock-valuation-concept',
    topic: 'Stock Valuation',
    keywords: ['stock', 'inventory', 'valuation', 'warehouse'],
    content: `Stock Valuation tracks inventory items using methods similar to:
- Running balance: Each purchase adds to quantity and amount
- Each sale reduces quantity and calculates the cost of goods sold
- Balance quantity and balance amount are tracked, giving an average cost per unit
- Useful for businesses that need to track inventory value over time`,
    category: 'accounting_concept',
  },

  // ============ HOW-TO GUIDES ============
  {
    id: 'how-to-create-transaction',
    topic: 'How to Create a Transaction',
    keywords: ['how to create transaction', 'new transaction', 'add transaction', 'record transaction'],
    content: `To create a new transaction:
1. First, select a record (from the Records page)
2. Navigate to Transactions page (/records/transactions)
3. Click "New Transaction" or go to /records/transactions/new
4. Fill in:
   - Date: Select the transaction date
   - Action: Choose what happened (e.g., "Sold", "Bought")
   - Object: Select what was affected
   - Indirect Object (optional): Select the secondary entity
   - Media: Select payment method
   - Quantity: Enter number of units
   - Total Amount: Enter monetary value
5. Submit the form

Tip: Use autocomplete when searching for entities. The system uses cosine similarity to find relevant matches.`,
    category: 'how_to',
  },
  {
    id: 'how-to-create-entity',
    topic: 'How to Create a Transaction Entity',
    keywords: ['how to create entity', 'new entity', 'add action', 'add object', 'add media'],
    content: `To create a new transaction entity:
1. Navigate to Transaction Entities page (/records/transaction-entities)
2. Click "New Entity" or open the sidebar form
3. Fill in:
   - Name: The entity name (e.g., "Sold", "Product A", "Cash")
   - Description (optional): A brief description
   - Category: Choose from Action, Object, Indirect Object, or Media
   - Type (optional): Choose from credit, debit, tradable, non_tradable, payable, third_party
   - Past Tense (optional): For actions, the past tense form (e.g., "Sold" for "Sell")
   - Link to KPI (optional): Whether this entity should appear in dashboard KPIs
4. Submit to create

Entities power the semantic richness of your transactions.`,
    category: 'how_to',
  },
  {
    id: 'how-to-create-record',
    topic: 'How to Create a Record',
    keywords: ['how to create record', 'new record', 'add record', 'create company book'],
    content: `To create a new record:
1. Go to Records page (/records)
2. Click "Create Record" or the create button
3. Fill in:
   - Record Name: A name for your record (e.g., "Company A Books", "2024 Accounts")
   - Description (optional): Brief description
   - Currency: Select the currency for this record
4. Submit

After creating, you'll be able to add transactions and view accounts within this record.`,
    category: 'how_to',
  },
  {
    id: 'how-to-share-record',
    topic: 'How to Share a Record with Other Users',
    keywords: ['share', 'permission', 'grant', 'collaborate', 'team', 'user access'],
    content: `To share a record with other users:
1. Go to Records page (/records)
2. Find the record you want to share
3. Open the Manage Permissions option for that record
4. Grant permissions by specifying:
   - Target user's ID or email
   - Read permission: Can view data
   - Write permission: Can edit/create/delete data
5. Submit to apply permissions

You can also share a join code with users so they can join the record themselves.`,
    category: 'how_to',
  },
  {
    id: 'how-to-view-balance-sheet',
    topic: 'How to View Balance Sheet',
    keywords: ['how to view balance sheet', 'balance sheet', 'financial position'],
    content: `To view the Balance Sheet:
1. Select a record first
2. Navigate to Accounts > Balance Sheets or go to /records/accounts/balance-sheets
3. Set the date range (start and end dates) - this is required
4. The balance sheet will display:
   - Assets: broken down by media (cash, bank, etc.), stock, prepaid, debitors
   - Liabilities: categorized by what is owed
   - Capital: owner's equity breakdown
5. Verify that Assets = Liabilities + Capital`,
    category: 'how_to',
  },
  {
    id: 'how-to-buy-credits',
    topic: 'How to Purchase Credits',
    keywords: ['buy credits', 'purchase credits', 'top up', 'add funds', 'payment'],
    content: `To purchase credit points:
1. Go to Credit Points page (/credit-points) to view your current balance
2. Click "Purchase Credits" or go to /credit/purchase
3. Select the amount of credits you want to buy
4. Choose payment method: Flutterwave or Stripe
5. Complete the payment process
6. Credits will be added to your balance automatically`,
    category: 'how_to',
  },
  {
    id: 'how-to-subscribe',
    topic: 'How to Subscribe to a Plan',
    keywords: ['subscribe', 'subscription', 'plan', 'upgrade', 'premium'],
    content: `To subscribe to a plan:
1. Go to Subscription page (/subscription)
2. View available plans and their features
3. Select a plan that meets your needs
4. Choose payment method (credit points or direct payment)
5. Complete the subscription process
6. Your active subscription will grant access to premium features

You can view your current subscription status on the Subscription page.`,
    category: 'how_to',
  },

  // ============ FAQ ============
  {
    id: 'faq-what-is-record',
    topic: 'What is a Record?',
    keywords: ['what is record', 'record meaning'],
    content: `A Record is like a separate ledger or company book. Each record holds its own set of transactions and accounts. You can have multiple records for different businesses, different years, or different accounting periods.`,
    category: 'faq',
  },
  {
    id: 'faq-currency-conversion',
    topic: 'Currency Codes vs Symbols',
    keywords: ['currency', 'symbol', 'code', 'conversion', 'USD', 'EUR'],
    content: `Balancio Pro uses currency codes internally (like USD, EUR, GHS) but displays currency symbols (like $, €, ₵) in the UI. When you see a currency symbol, it corresponds to a standard currency code.`,
    category: 'faq',
  },
  {
    id: 'faq-auth-pages',
    topic: 'Authentication Pages',
    keywords: ['login', 'signup', 'forgot password', 'auth', 'authentication'],
    content: `Authentication pages (Login, Signup, Forgot Password) do not show the sidebar or navbar. Once logged in, the full app layout with navigation is displayed.`,
    category: 'faq',
  },
];

export function searchKnowledge(query: string): KnowledgeEntry[] {
  const searchTerms = query.toLowerCase().split(/\s+/).filter(t => t.length > 2);
  const results: Array<{ entry: KnowledgeEntry; score: number }> = [];

  for (const entry of knowledgeBase) {
    let score = 0;
    const searchText = [
      entry.topic,
      entry.content,
      ...entry.keywords,
    ].join(' ').toLowerCase();

    for (const term of searchTerms) {
      if (searchText.includes(term)) {
        score += 1;
        // Bonus for matching keywords
        if (entry.keywords.some(k => k.toLowerCase().includes(term))) {
          score += 2;
        }
        // Bonus for matching topic
        if (entry.topic.toLowerCase().includes(term)) {
          score += 3;
        }
      }
    }

    if (score > 0) {
      results.push({ entry, score });
    }
  }

  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)
    .map(r => r.entry);
}

export function getKnowledgeByCategory(category: KnowledgeEntry['category']): KnowledgeEntry[] {
  return knowledgeBase.filter(k => k.category === category);
}

export function formatKnowledgeForPrompt(entries: KnowledgeEntry[]): string {
  return entries.map(e =>
    `[${e.topic}]: ${e.content}`
  ).join('\n\n');
}
